import {
  prepareEstimatePdfImages,
  mergeEstimatePdfImages,
  type PdfImageReadOptions,
} from '@/lib/sales/prepare-pdf-line-images';
import type {
  AttachmentImage,
  PdfCompanyInfo,
  PdfCustomFieldValue,
} from '@/components/documents/estimates/pdfTypes';
import type { PublicPaymentPlanPreview } from '@/components/public/PaymentPlanPreview';
import type { Estimate } from '@/types/estimate';
import type { PublicWisetackCta } from '@/types/public';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';
import type { EstimateDocTotalsOverride } from '@/components/documents/estimates/blocks/EstimateDocumentRenderer';

/**
 * The exact prop set `EstimateDocumentPdf` renders the customer-facing PDF from.
 * `timeZone` is optional and defaults to the renderer's locale tz — the public
 * quote page omits it (unchanged behaviour); the client portal passes the
 * resolved company tz so document dates match the rest of the portal.
 */
export interface SignedPdfContent {
  estimate: Estimate;
  company?: PdfCompanyInfo | null;
  signatureDataUrl?: string;
  attachmentImages?: AttachmentImage[];
  customFields?: PdfCustomFieldValue[];
  wisetack?: PublicWisetackCta | null;
  paymentPlan?: PublicPaymentPlanPreview | null;
  serviceFee?: {
    show: boolean;
    percent: number;
    chargedAmount?: number | null;
  } | null;
  timeZone?: string | null;
  /** Resolved document-template layout — when present, the PDF renders through it. */
  resolvedDocLayout?: EstimateDocLayout | null;
  /** Authoritative totals for the accepted-membership PDF; see EstimateDocumentPdf. */
  totalsOverride?: EstimateDocTotalsOverride;
  /** List-priced selection for the design PDF's line items; see EstimateDocumentPdf. */
  designEstimate?: Estimate;
  /** "Prepared by" party for the single-scope design PDF (public estimates strip
   *  the technician name off the estimate, so it is threaded explicitly). */
  presentedByName?: string | null;
}

/**
 * Render the customer-facing estimate PDF in the browser, upload it to S3, and
 * register it as the estimate's document of record — the SINGLE implementation
 * shared by the public quote page and the client portal, so the signed PDF can
 * never drift between the two surfaces (the exact drift the review bot flagged
 * when the portal carried its own reduced copy).
 *
 * Every network step is verified and throws on failure; the caller decides
 * whether a failure is fatal (signature approvals — the PDF *is* the consent
 * record) or best-effort (online approvals, and the post-reprice re-register).
 *
 * The PDF endpoints are the same `@Public()`-by-id endpoints both surfaces use
 * (`/estimates/public/:id/pdf-upload-url`, `/estimates/public/:id/pdf`); the
 * portal caller has already proven ownership of `id` before getting here.
 *
 * `@react-pdf/renderer` + the PDF component are dynamically imported so this
 * heavy dependency stays out of the initial bundle — matching the original
 * inline flow on the public page.
 */
export async function renderSignedPdf(
  content: SignedPdfContent,
  imageRead: PdfImageReadOptions = {},
): Promise<Blob> {
  const estimate = await prepareEstimatePdfImages(content.estimate, imageRead);
  const designEstimate = content.designEstimate
    ? mergeEstimatePdfImages(content.designEstimate, estimate)
    : undefined;
  const [{ pdf }, { EstimateDocumentPdf }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('@/components/documents/estimates/EstimateDocumentPdf'),
  ]);
  return pdf(
    <EstimateDocumentPdf
      estimate={estimate}
      acceptedScopeOnly
      company={content.company}
      signatureDataUrl={content.signatureDataUrl}
      attachmentImages={content.attachmentImages}
      customFields={content.customFields}
      wisetack={content.wisetack}
      paymentPlan={content.paymentPlan}
      serviceFee={content.serviceFee}
      timeZone={content.timeZone}
      resolvedDocLayout={content.resolvedDocLayout}
      totalsOverride={content.totalsOverride}
      designEstimate={designEstimate}
      presentedByName={content.presentedByName}
    />,
  ).toBlob();
}

export async function persistSignedPdf(params: {
  opsBaseUrl: string;
  id: string;
  reason: string;
  blob: Blob;
  /** Receives the rendered blob (e.g. to show it on-screen as the record). */
  onBlob?: (blob: Blob) => void;
}): Promise<void> {
  const { opsBaseUrl, id, reason, blob, onBlob } = params;
  onBlob?.(blob);

  const uploadRes = await fetch(`${opsBaseUrl}/estimates/public/${id}/pdf-upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!uploadRes.ok) {
    throw new Error(`Could not get upload URL (${uploadRes.status})`);
  }
  const { uploadUrl, fileUrl } = await uploadRes.json();

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'application/pdf' },
  });
  if (!putRes.ok) {
    throw new Error(`Signed PDF upload failed (${putRes.status})`);
  }

  const registerRes = await fetch(`${opsBaseUrl}/estimates/public/${id}/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: fileUrl, reason }),
  });
  if (!registerRes.ok) {
    throw new Error(`Could not save signed PDF (${registerRes.status})`);
  }
}

export async function registerSignedPdf(params: {
  opsBaseUrl: string;
  id: string;
  reason: string;
  content: SignedPdfContent;
  /** Receives the rendered blob (e.g. to show it on-screen as the record). */
  onBlob?: (blob: Blob) => void;
}): Promise<void> {
  const { content, ...persistParams } = params;
  const blob = await renderSignedPdf(content, { opsBaseUrl: params.opsBaseUrl, id: params.id });
  await persistSignedPdf({ ...persistParams, blob });
}
